import React from 'react';
import {ResponsivePie} from '@nivo/pie';

import './ExitsAfterAnswer.scss';
import {Card} from '@app/components/Card/Card';
import {CardTitle} from '@app/components/CardTitle/CardTitle';
import {ChartLegend} from '@app/components/ChartLegend/ChartLegend';
import {RadialContentLoader} from '@app/components/ContentLoaders/RadialContentLoader';
import {ErrorLoadingWrapper} from '@app/components/ErrorLoadingWrapper/ErrorLoadingWrapper';
import {Call} from '@app/state';
import {ExitPageMetric} from '@app/state/ExitPage';
import {
    calcRatio,
    COLOR_DATAVIZ_GREEN,
    COLOR_DATAVIZ_ORANGE,
    COLOR_DATAVIZ_BLUE,
} from '@app/utils/Common';

export interface ExitsAfterAnswerProps {
    metrics: Call<ExitPageMetric | null>;
}

export const ExitsAfterAnswer: React.FC<ExitsAfterAnswerProps> = (props) => {
    const getEmptyState = () => {
        if (props.metrics.loading) {
            return null;
        }

        if (
            !props.metrics.data ||
            !props.metrics.data.counters ||
            props.metrics.data.counters.answers === 0
        ) {
            return 'No data';
        }

        return null;
    };

    return (
        <Card>
            <CardTitle title="Exits after an answer" />
            <ErrorLoadingWrapper
                height={'200px'}
                error={props.metrics.error ? props.metrics.error : ''}
                isLoading={props.metrics.loading}
                CustomLoading={RadialContentLoader}
                emptyState={getEmptyState()}
                render={() => {
                    return (
                        <div className="ExitsAfterAnswer__container">
                            <div className="ExitsAfterAnswer__chartContainer">
                                <ResponsivePie
                                    data={[
                                        {
                                            id: 'responses',
                                            label: 'Responses',
                                            value:
                                                props.metrics.data?.counters?.[
                                                    'answers.responses'
                                                ] || 0,
                                            color: COLOR_DATAVIZ_BLUE,
                                        },
                                        {
                                            id: 'documents',
                                            label: 'Documents',
                                            value:
                                                props.metrics.data?.counters?.[
                                                    'answers.contents'
                                                ] || 0,
                                            color: COLOR_DATAVIZ_ORANGE,
                                        },
                                        {
                                            id: 'chatter',
                                            label: 'Chatter',
                                            value:
                                                props.metrics.data?.counters?.[
                                                    'answers.chatterings'
                                                ] || 0,
                                            color: COLOR_DATAVIZ_GREEN,
                                        },
                                    ]}
                                    margin={{
                                        top: 20,
                                        right: 20,
                                        bottom: 20,
                                        left: 20,
                                    }}
                                    sortByValue={false}
                                    innerRadius={0.7}
                                    padAngle={1}
                                    colors={(c: any) => c.data.color as string}
                                    borderWidth={0}
                                    enableArcLabels={false}
                                    enableArcLinkLabels={false}
                                    isInteractive={false}
                                />
                            </div>
                            <ChartLegend
                                items={[
                                    {
                                        color: COLOR_DATAVIZ_BLUE,
                                        title: 'Responses',
                                        value:
                                            props.metrics.data?.counters?.[
                                                'answers.responses'
                                            ] || 0,
                                        percentage: calcRatio(
                                            props.metrics.data?.counters?.[
                                                'answers.responses'
                                            ] || 0,
                                            props.metrics.data?.counters
                                                ?.answers || 0,
                                        ),
                                    },
                                    {
                                        color: COLOR_DATAVIZ_ORANGE,
                                        title: 'Documents',
                                        value:
                                            props.metrics.data?.counters?.[
                                                'answers.contents'
                                            ] || 0,
                                        percentage: calcRatio(
                                            props.metrics.data?.counters?.[
                                                'answers.contents'
                                            ] || 0,
                                            props.metrics.data?.counters
                                                ?.answers || 0,
                                        ),
                                    },
                                    {
                                        color: COLOR_DATAVIZ_GREEN,
                                        title: 'Chatter',
                                        value:
                                            props.metrics.data?.counters?.[
                                                'answers.chatterings'
                                            ] || 0,
                                        percentage: calcRatio(
                                            props.metrics.data?.counters?.[
                                                'answers.chatterings'
                                            ] || 0,
                                            props.metrics.data?.counters
                                                ?.answers || 0,
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    );
                }}
            />
        </Card>
    );
};
